import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import ErrorState from "components/Error/ErrorState";
import LoadingIcon from "components/Loading/LoadingIcon";
import Table from "components/Table/Table";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useState } from "react";
import type { RenderCellProps } from "react-data-grid";
import { useParams } from "react-router-dom";
import { setShowAddModal } from "reducers/main";
import {
  useDeleteAdjusterByIdMutation,
  useGetAdjustersQuery,
} from "services/adjuster-api";
import { useDispatch, useSelector } from "utils/redux";
import Tooltip from "components/Tooltip/Tooltip";

import { EditAdjusterDetailsModal } from "./EditAdjusterDetailsModal";

import type { AdjusterType } from "types/DataTypes";

export const AdjusterTable = () => {
  const dispatch = useDispatch();
  const { clientId } = useParams<{ clientId?: string }>();
  const showAddModal = useSelector((state) => state.main.showAddModal);
  const [currentAdjusterId, setCurrentAdjusterId] = useState<number>();
  const [adjusterToDelete, setAdjusterToDelete] = useState<AdjusterType | null>(
    null,
  );

  const {
    data: adjusters = { results: [], count: 0 },
    isLoading,
    error,
  } = useGetAdjustersQuery({ client_id: clientId }, { skip: !clientId });

  const [deleteAdjusterById] = useDeleteAdjusterByIdMutation();

  const handleDeleteClick = (adjuster: AdjusterType) => {
    setAdjusterToDelete(adjuster);
  };

  const handleDeleteConfirm = async () => {
    if (!adjusterToDelete) return;

    try {
      await deleteAdjusterById({
        id: adjusterToDelete.id,
      }).unwrap();
      setAdjusterToDelete(null);
    } catch (error) {
      KaboodleToaster.error("Error deleting adjuster");
      console.error(error);
    }
  };

  const handleAddClick = () => {
    setCurrentAdjusterId(undefined);
    dispatch(setShowAddModal(true));
  };

  const columns = [
    { key: "name", name: "Your Adjusters" },
    {
      key: "actions",
      name: "Actions",
      width: 120,
      renderCell(props: RenderCellProps<AdjusterType>) {
        return (
          <div className="action-column">
            <ButtonIcon
              className="btn-icon"
              type="button"
              onClick={() => {
                dispatch(setShowAddModal(true));
                setCurrentAdjusterId(props.row.id);
              }}
            >
              edit
            </ButtonIcon>
            <ButtonIcon
              className="btn-icon delete"
              type="button"
              onClick={() => handleDeleteClick(props.row)}
            >
              delete
            </ButtonIcon>
          </div>
        );
      },
    },
  ];

  const rowKeyGetter = (row: AdjusterType) => row.id;

  if (isLoading) {
    return <LoadingIcon />;
  }

  if (error) {
    return <ErrorState />;
  }

  return (
    <>
      <EditAdjusterDetailsModal
        isOpen={showAddModal}
        onClose={() => dispatch(setShowAddModal(false))}
        adjusterId={currentAdjusterId}
        clientId={clientId}
      />
      <Card
        heading={
          <div className="flex jc-sb ai-c">
            <span>Adjusters</span>
            <Tooltip content="Add new adjuster" place="left" variant="info">
              <ButtonIcon
                kind="neutral"
                size="small"
                type="button"
                onClick={handleAddClick}
              >
                add_circle
              </ButtonIcon>
            </Tooltip>
          </div>
        }
        type="full-width"
      >
        <Table
          rowKeyGetter={rowKeyGetter}
          rows={adjusters.results}
          columns={columns}
          className="adjuster-table"
        />
      </Card>

      {adjusterToDelete && (
        <KaboodleModal
          isOpen={!!adjusterToDelete}
          onClose={() => setAdjusterToDelete(null)}
          footer={
            <div className="flex jc-c">
              <Button className="delete" onClick={handleDeleteConfirm}>
                Confirm Delete
              </Button>
              <Button onClick={() => setAdjusterToDelete(null)}>Cancel</Button>
            </div>
          }
        >
          <Card heading="Delete Adjuster">
            <p>Are you sure you want to delete {adjusterToDelete.name}?</p>
          </Card>
        </KaboodleModal>
      )}
    </>
  );
};
